import {original} from 'immer';
import {getType} from 'typesafe-actions';

import {isSparseOverridesEnabled} from '../../util/sparseOverridesFeatureFlag';
import {setInShallowClone} from '../../util/utility';
import * as PanelActions from './panel/actions';
import * as PanelBankConfigActions from './panelBankConfig/actions';
import * as PanelBankConfigActionsInternal from './panelBankConfig/actionsInternal';
import * as ConfigOverrideUtils from './panelBankConfig/configOverrideUtils';
import * as PanelBankSectionConfigActions from './panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from './panelBankSectionConfig/types';
import {type ActionType, type ViewReducerState} from './reducerSupport';

// These functions handle actions that should flag section(s) as `isPanelsAuto: false`.
// There's one for actions processed by immer, and another for ones processed immutably.
// This occurs after the action has been processed in the main action handler.
//
// If an action is also used to undo/redo itself or another action, we must revert the
// flag to its previous value rather than always setting to false; in these cases, an
// `isPanelsAutoForUndoRedo?: {value: boolean | undefined}` must be provided in the
// action payload. Note that `undefined` is a valid value for `isPanelsAuto`, and we
// must distinguish between "set to undefined" and "not provided, set to false by
// default". Actions that have dedicated undo actions (e.g. `undoDeletePanels`) will
// handle reverting the flag directly in the main action handler.
//
// The `addPanelsBySpec` action (for global quick add) is a special case that handles
// setting `isPanelsAuto` in the main action handler. It's similar to `diffAndInitPanels`
// (for auto panel gen), and it's easier to embed `isPanelsAuto` logic directly there.

export const markSectionPanelsAutoImmer = (
  draft: ViewReducerState,
  action: ActionType,
  inverseAction: ActionType
) => {
  let sectionRefs: PanelBankSectionConfigTypes.Ref[] = []; // sections that need to be marked
  let isPanelsAuto: boolean | undefined = false; // new isPanelsAuto value to use

  // get section refs
  switch (action.type) {
    case getType(PanelBankConfigActions.addSection): {
      if (
        inverseAction.type === getType(PanelBankConfigActions.deleteSection)
      ) {
        sectionRefs = [inverseAction.payload.sectionRef];
      }
      break;
    }
    case getType(PanelBankSectionConfigActions.duplicatePanel): {
      // When sparse overrides are enabled, the duplicate is marked as a custom
      // panel (isAuto=false) with a unique key, and gets a placement override.
      // The section can stay auto.
      if (!isSparseOverridesEnabled()) {
        sectionRefs = [action.payload.ref];
      }
      break;
    }
    case getType(PanelBankSectionConfigActions.insertUpdatedPanel): {
      // Check eligibility on the original state (before immer mutation).
      const {ref: sectionRef, panelRef, panelBankConfigRef} = action.payload;
      const originalState = original(draft) as ViewReducerState;

      const {eligible} = ConfigOverrideUtils.isPanelEligibleForConfigOverride(
        originalState,
        panelRef,
        sectionRef,
        panelBankConfigRef
      );

      if (
        isSparseOverridesEnabled() &&
        eligible &&
        panelBankConfigRef != null
      ) {
        // Panel is eligible for sparse override - restore section to auto
        sectionRefs = [sectionRef];
        isPanelsAuto = true;
      } else {
        // Not eligible or feature disabled - mark section as non-auto (legacy behavior)
        sectionRefs = [sectionRef];
      }
      break;
    }
    case getType(PanelBankSectionConfigActions.deletePanel): {
      sectionRefs = [action.payload.ref];
      if (action.payload.isPanelsAutoForUndoRedo) {
        isPanelsAuto = action.payload.isPanelsAutoForUndoRedo.value;
      }
      break;
    }
    case getType(PanelActions.deletePanels): {
      if (inverseAction.type === getType(PanelActions.undoDeletePanels)) {
        const {deletePanelsResult} = inverseAction.payload;
        sectionRefs = deletePanelsResult.map(({sectionRef}) => sectionRef);
      }
      if (action.payload.isPanelsAutoForUndoRedo) {
        isPanelsAuto = action.payload.isPanelsAutoForUndoRedo.value;
      }
      break;
    }
    case getType(PanelBankConfigActions.movePanelImmer):
    case getType(PanelBankConfigActions.movePanelToNewSection): {
      if (
        inverseAction.type ===
        getType(PanelBankConfigActionsInternal.undoMovePanelImmer)
      ) {
        const {toSectionRef, fromSectionRef} = inverseAction.payload;
        sectionRefs = [toSectionRef, fromSectionRef];
      }
      break;
    }
  }

  // update isPanelsAuto values
  sectionRefs.forEach(sectionRef => {
    draft.parts[sectionRef.type][sectionRef.id].isPanelsAuto = isPanelsAuto;
  });
};

export const markSectionPanelsAutoImmutable = (
  state: ViewReducerState,
  action: ActionType,
  inverseAction: ActionType
): ViewReducerState => {
  let sectionRefs: PanelBankSectionConfigTypes.Ref[] = []; // sections that need to be marked
  let isPanelsAuto: boolean | undefined = false; // new isPanelsAuto value to use

  // get section refs
  switch (action.type) {
    case getType(PanelBankSectionConfigActions.addPanelByRef):
    case getType(PanelBankSectionConfigActions.addPanelByConfig):
    case getType(PanelBankSectionConfigActions.addPanelsByConfig): {
      // With selective persistence, custom panels are persisted individually
      // so we don't need to mark the section non-auto when adding panels.
      if (!isSparseOverridesEnabled()) {
        sectionRefs = [action.payload.ref];
      }
      break;
    }
    case getType(PanelBankSectionConfigActions.updateName): {
      sectionRefs = [action.payload.ref];
      if (action.payload.isPanelsAutoForUndoRedo) {
        isPanelsAuto = action.payload.isPanelsAutoForUndoRedo.value;
      }
      break;
    }
    // setConfig doubles as the undo action for updateConfig. On the sparse path,
    // the eligibility check here sees the post-edit state (still auto) and keeps
    // isPanelsAuto=true. The override swap itself is handled in undoableActions.
    case getType(PanelActions.setConfig):
    case getType(PanelActions.updateConfig): {
      const {ref, panelBankSectionConfigRef, panelBankConfigRef} =
        action.payload;
      if (panelBankSectionConfigRef) {
        // When used as an undo/redo action, restore the previous isPanelsAuto value
        if (
          action.type === getType(PanelActions.setConfig) &&
          action.payload.isPanelsAutoForUndoRedo
        ) {
          sectionRefs = [panelBankSectionConfigRef];
          isPanelsAuto = action.payload.isPanelsAutoForUndoRedo.value;
        } else {
          const {eligible} = isSparseOverridesEnabled()
            ? ConfigOverrideUtils.isPanelEligibleForConfigOverride(
                state,
                ref,
                panelBankSectionConfigRef,
                panelBankConfigRef
              )
            : {eligible: false};

          if (eligible && panelBankConfigRef != null) {
            // Panel is eligible for sparse override - RESTORE section to auto
            sectionRefs = [panelBankSectionConfigRef];
            isPanelsAuto = true;
          } else {
            // Not eligible or feature disabled - mark section as non-auto (legacy behavior)
            sectionRefs = [panelBankSectionConfigRef];
            // isPanelsAuto stays false (the default)
          }
        }
      }
      break;
    }
  }

  // update isPanelAuto values
  let newState = state;
  sectionRefs.forEach(sectionRef => {
    newState = setInShallowClone(
      newState,
      ['parts', sectionRef.type, sectionRef.id, 'isPanelsAuto'],
      isPanelsAuto
    );
  });

  return newState;
};
